import React, {
  FC,
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
  useMemo,
  memo,
} from "react";
import {
  Disbursement,
  Discount,
  MatterFilter,
  Phase,
  Uplift,
  PracticeArea,
  PhaseBase,
  RelevantMatter,
  Matter,
  MatterScopeEditor,
  MatterAssumptionsEditor,
  MatterExclusionsEditor,
  MatterDisbursementsEditor,
  MatterUpliftsEditor,
  MatterDiscountsEditor,
} from "@design-system";
import { get as levenshtein } from "fast-levenshtein";
import { RelevantMatterPage } from "../../design-system/components/create-proposal/relevant-matter-page";
import { PricingLevel, PricingType } from "../../graphql/__generated_types__/globalTypes";
import {
  FindAccruedFeesForMatterQuery,
  FindAccruedFeesForMatterQueryVariables,
  TaxLabel,
  TeamMembershipPartsFragment,
} from "../../gql/graphql";
import { Tabs, Button, Modal } from "antd";
import { ScopeAndAdjustmentsLibrary, LibraryItemRenderer } from "./scope-and-adjustments-library";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import {
  useRelevantMatterSearch,
  useFindMatterQuery,
  useFilteredMattersQuery,
} from "~/hooks/matters";
import {
  SimpleStringListRenderer,
  LibraryListRenderer,
  GroupedListRenderer,
  SuggestedLibraryListRenderer,
} from "./library-renderers";
import {
  useLibraryAssumptions,
  useLibraryDiscounts,
  useLibraryUplifts,
  useLibraryDisbursements,
  useLibraryExclusions,
} from "./hooks/use-library-data";
import {
  useDiscountMutations,
  useUpliftMutations,
  useAssumptionsMutations,
  useExclusionsMutations,
  useDisbursementMutations,
  useScopeOfWorkMutations,
} from "./hooks/use-scope-mutations";
import { GlobalContext } from "~/context/global-context";
import { useSettings } from "../../hooks";
import { SubTabType, LibraryItem, TabItemMap } from "./types/library-item-types";
import { LibraryMatterTypeObj, usePaginatedMatterQuery } from "~/hooks/use-paginated-matter-query";
import { COPY_RESOURCING, COPY_PRICING, UPDATE_MATTER, COPY_SCOPE } from "~/graphql/mutation";
import { FILTER_MATTERS } from "~/graphql/query/filtered-matters-query";
import { useMutation, useQuery } from "@apollo/client";

import styles from "./scope-and-adjustments-tab-container.css";
import { produce } from "immer";
import {
  FIND_MATTER,
  MATTER_ASSUMPTIONS_QUERY,
  MATTER_EXCLUSIONS_QUERY,
  FIND_ACCRUED_FEES_FOR_MATTER,
} from "~/graphql/query";

const {
  "scope-and-adjustments-container": scopeAndAdjustmentsContainerClassName,
  "scope-and-adjustments-content": scopeAndAdjustmentsContentClassName,
  "scope-and-adjustments-main": scopeAndAdjustmentsMainClassName,
  "scope-sub-tabs": scopeSubTabsClassName,
  "scope-library-wrapper": scopeLibraryWrapperClassName,
  "scope-library-wrapper-collapsed": scopeLibraryWrapperCollapsedClassName,
  "scope-library-toggle": scopeLibraryToggleClassName,
  "tab-content": tabContentClassName,
} = styles as any;

const INITIAL_MATTER_FILTER: MatterFilter = {
  title: null,
  client: null,
  orgUnit: null,
  status: null,
  updatedAt: null,
  orderBy: null,
  createdBy: null,
  currencyId: null,
  feeEarner: null,
  someTimeEntries: true,
  linked: null,
};

type Props = {
  // Common props
  matterId: number;
  currencySymbol: string;
  organisationalUnitId?: number | null;

  // ScopeTabContainer props
  title?: string;
  template?: boolean;
  phases?: Phase[] | null | undefined;
  pricingLevel: PricingLevel;
  pricingType: PricingType;
  countryCode?: string;
  teamMembers?: TeamMembershipPartsFragment[];
  handleRefetchSuggestions?: () => Promise<void>;
  practiceAreas: PracticeArea[];

  // AssumptionsContainer props
  suggestedAssumptions?: { chosen: string[]; suggested: string[] };

  // ExclusionsContainer props
  suggestedExclusions?: { chosen: string[]; suggested: string[] };

  // DisbursementsTabContainer props
  disbursements: Disbursement[];
  excludeTax: boolean;
  taxLevelPercent: number;
  taxLabel: TaxLabel;

  // UpliftsTabContainer props
  uplifts: Uplift[];

  // DiscountsTabContainer props
  discounts: Discount[];

  setTopDownRefreshTrigger: React.Dispatch<React.SetStateAction<number>> | undefined;
};

// Memoize the ScopeLibrary component to prevent unnecessary re-renders
const MemoizedScopeAndAdjustmentsLibrary = memo(ScopeAndAdjustmentsLibrary);
const MemoizedLibraryListRenderer = memo(LibraryListRenderer);
const MemoizedGroupedListRenderer = memo(GroupedListRenderer);
const MemoizedSimpleStringListRenderer = memo(SimpleStringListRenderer);
const MemoizedSuggestedLibraryListRenderer = memo(SuggestedLibraryListRenderer);

// Memoize editor components to prevent unnecessary re-renders
const MemoizedMatterScopeEditor = memo(MatterScopeEditor);
const MemoizedMatterAssumptionsEditor = memo(MatterAssumptionsEditor);
const MemoizedMatterExclusionsEditor = memo(MatterExclusionsEditor);
const MemoizedMatterDisbursementsEditor = memo(MatterDisbursementsEditor);
const MemoizedMatterUpliftsEditor = memo(MatterUpliftsEditor);
const MemoizedMatterDiscountsEditor = memo(MatterDiscountsEditor);

// Dedicated component for matter search functionality
const MatterSearchProvider = memo(
  ({
    searchText,
    matterFilter,
    searchOptions,
    onDataLoaded,
  }: {
    searchText: string;
    matterFilter: MatterFilter;
    searchOptions: {
      additionalSearchText: string | undefined;
      autoRetry: boolean;
      fetchTemplates: boolean;
      excludeMatterId: number;
    };
    onDataLoaded: (data: {
      historicalData: RelevantMatter[];
      historicalLoading: boolean;
      triggerHistoricalSearch: () => void;
      templatesData: RelevantMatter[];
      templatesLoading: boolean;
    }) => void;
  }) => {
    const searchResults = useRelevantMatterSearch(searchText, matterFilter, searchOptions);

    useEffect(() => {
      onDataLoaded({
        historicalData: searchResults.historicalData?.findRelevantMatters?.matters || [],
        historicalLoading: searchResults.historicalLoading || false,
        triggerHistoricalSearch: searchResults.triggerSearch,
        templatesData: searchResults.templatesData?.findRelevantMatters?.matters || [],
        templatesLoading: searchResults.templatesLoading || false,
      });
    }, [
      searchResults.historicalData,
      searchResults.historicalLoading,
      searchResults.triggerSearch,
      searchResults.templatesData,
      searchResults.templatesLoading,
      onDataLoaded,
    ]);

    return null;
  },
  // Custom comparison function to prevent unnecessary re-renders
  (prevProps, nextProps) => {
    // Only re-render if the actual search text content changes
    const searchTextChanged = prevProps.searchText !== nextProps.searchText;

    // Check if matter filter has meaningful changes
    const orgUnitChanged = prevProps.matterFilter.orgUnit !== nextProps.matterFilter.orgUnit;

    // Check if search options have meaningful changes
    const additionalSearchText = nextProps.searchOptions.additionalSearchText;
    const prevAdditionalSearchText = prevProps.searchOptions.additionalSearchText;
    const additionalSearchTextChanged = prevAdditionalSearchText !== additionalSearchText;

    // Only re-render if one of these key properties changed
    const shouldRerender = searchTextChanged || orgUnitChanged || additionalSearchTextChanged;

    // Return true if props are equal (no re-render needed)
    return !shouldRerender;
  }
);

// Add a TabCache component that preserves component state when switching tabs
const TabCache = memo(({ active, children }: { active: boolean; children: React.ReactNode }) => {
  return (
    <div
      className={tabContentClassName}
      style={{
        display: active ? "block" : "none",
        position: active ? "relative" : "absolute",
        visibility: active ? "visible" : "hidden",
        width: "100%",
      }}
    >
      {children}
    </div>
  );
});

export const ScopeAndAdjustmentsTabContainer: FC<Props> = ({
  // Common props
  matterId,
  currencySymbol,
  organisationalUnitId,

  // ScopeTabContainer props
  title,
  template,
  phases,
  pricingLevel,
  pricingType,
  countryCode,
  teamMembers,
  handleRefetchSuggestions,
  practiceAreas,

  // AssumptionsContainer props
  suggestedAssumptions,

  // ExclusionsContainer props
  suggestedExclusions,

  // DisbursementsTabContainer props
  disbursements,
  excludeTax,
  taxLevelPercent,
  taxLabel,

  // UpliftsTabContainer props
  uplifts,

  // DiscountsTabContainer props
  discounts,

  setTopDownRefreshTrigger,
}) => {
  // State to keep track of the active sub-tab
  const [activeSubTab, setActiveSubTab] = useState<SubTabType>("scope");
  // State to track if the component is mounted to prevent flickering
  const [mounted, setMounted] = useState(false);
  // State to track if the sidebar is collapsed
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const buildInitialSearchText = () => {
    if (!phases?.length) return `Matter Title: ${title || ""}`;
    return `Matter Title: ${title}\n\n${phases
      ?.map((phase) => {
        return `  - Phase: ${phase.title}\n    Tasks:\n${phase.tasks
          ?.map((task) => `      - ${task.title}\n`)
          .join("")}`;
      })
      .join("\n")}`;
  };
  const [mainSearchText, setMainSearchText] = useState(buildInitialSearchText);

  // Two separate search text states
  const [librarySearchText, setLibrarySearchText] = useState(""); // For filtering library items in non-scope tabs
  const [matterSearchText, setMatterSearchText] = useState(""); // Persistent search for templates and historical matters

  const [selectedHistoricalMatterId, setSelectedHistoricalMatterId] = useState<number | null>(null);
  const [selectedHistoricalMatter, setSelectedHistoricalMatter] = useState<Matter | null>(null);
  const [viewingHistoricalData, setViewingHistoricalData] = useState(false);

  const [selectedTemplateMatterId, setSelectedTemplateMatterId] = useState<number | null>(null);
  const [selectedTemplateMatter, setSelectedTemplateMatter] = useState<Matter | null>(null);
  const [viewingTemplateData, setViewingTemplateData] = useState(false);
  const [templateMatterLoading, setTemplateMatterLoading] = useState(false);
  const [matterFilter, setMatterFilter] = useState<MatterFilter>({
    ...INITIAL_MATTER_FILTER,
    orgUnit: organisationalUnitId ? organisationalUnitId : null,
  });

  const [selectedOrgUnitId, setSelectedOrgUnitId] = useState<number | null>(
    organisationalUnitId ?? null
  );

  // Fetch favourite matters
  const {
    matters: favouritesData,
    count: favouritesCount,
    loading: favouritesLoading,
    page: favouritesPage,
    pageSize: favouritesPageSize,
    onChangePage: onChangeFavouritesPage,
    onChangeTitleSearch,
  } = usePaginatedMatterQuery(
    LibraryMatterTypeObj.FAVOURITE,
    { pageSize: 5, titleFilter: matterSearchText },
    matterId
  );

  // Fetch pinned matters
  const { data: pinnedData, loading: pinnedLoading } = useFilteredMattersQuery({
    filter: {
      pinnedToId: matterId,
    },
    limit: 100,
  });

  // Use refs to track previous values
  const prevMatterSearchTextRef = useRef(matterSearchText);

  // Resubmit the favourites query when matterSearchText changes
  useEffect(() => {
    // Only update if value actually changed and not on first render
    if (prevMatterSearchTextRef.current !== matterSearchText) {
      prevMatterSearchTextRef.current = matterSearchText;
      // Don't trigger on initial render
      if (prevMatterSearchTextRef.current !== undefined) {
        onChangeTitleSearch(matterSearchText);
      }
    }
  }, [matterSearchText]);

  // Get library data
  const { libraryAssumptions } = useLibraryAssumptions(selectedOrgUnitId ?? undefined);
  const { libraryExclusions } = useLibraryExclusions(selectedOrgUnitId ?? undefined);
  const { libraryDisbursements } = useLibraryDisbursements();
  const { libraryUplifts } = useLibraryUplifts();
  const { libraryDiscounts } = useLibraryDiscounts();

  // Get mutations for adding to matter
  const {
    matterAssumptions,
    loading: { matterAssumptionsLoading },
    onAddNew: addAssumption,
    onCopy: copyAssumption,
    onSave: saveAssumption,
    onDelete: deleteAssumption,
    onReorder: reorderAssumptions,
  } = useAssumptionsMutations(matterId);
  const {
    matterExclusions,
    loading: { matterExclusionsLoading },
    onAddNew: addExclusion,
    onCopy: copyExclusion,
    onSave: saveExclusion,
    onDelete: deleteExclusion,
    onReorder: reorderExclusions,
  } = useExclusionsMutations(matterId);
  const {
    createDisbursementFromLibrary,
    deleteDisbursement,
    updateDisbursement,
    onReorder: onReorderDisbursements,
    autoFocusOff: autoFocusOffDisbursements,
  } = useDisbursementMutations(matterId);
  const {
    createUpliftFromLibrary,
    deleteUplift,
    updateUplift,
    onReorder: onReorderUplifts,
    autoFocusOff: autoFocusOffUplifts,
  } = useUpliftMutations(matterId);
  const {
    createDiscountFromLibrary,
    deleteDiscount,
    updateDiscount,
    onReorder: onReorderDiscounts,
    autoFocusOff: autoFocusOffDiscounts,
  } = useDiscountMutations(matterId);
  const {
    // Helper functions
    autoFocusOff,
    sortById,
    sortByOrder,
    // Reordering functions
    onReorderPhase,
    onReorderTask,
    // Wrapped functionality
    handleCreatePhase,
    handleCreatePhases,
    handleUpdatePhase,
    handleDeletePhase,
    handleCreateTask,
    handleCreateTasks,
    handleDeleteTasks,
    handleDeleteTask,
    handleUpdateTask,
    handleCreateFeeEarner,
    handleDeleteFeeEarner,
    handleUpdateFeeEarner,
    handleResetPhases,
    handleUpdatePhasePrice,
    // Copy phases functions
    copyPhasesFromMatter,
    copyPhaseToMatter,
  } = useScopeOfWorkMutations(matterId);

  const [updateMatter] = useMutation(UPDATE_MATTER);
  const [copyScope] = useMutation(COPY_SCOPE, {
    refetchQueries: [MATTER_ASSUMPTIONS_QUERY, MATTER_EXCLUSIONS_QUERY],
  });
  const [copyResourcing] = useMutation(COPY_RESOURCING);
  const [copyPricing] = useMutation(COPY_PRICING, {
    update(cache, { data }) {
      const copyPricing = data?.copyPricing;
      if (copyPricing) {
        cache.updateQuery({ query: FIND_MATTER, variables: { id: matterId } }, (currentData) => {
          if (currentData?.findMatter) {
            return produce(currentData, (draft) => {
              draft.findMatter.teamMemberships = copyPricing.teamMemberships;
              draft.findMatter.phases = copyPricing.phases;
              draft.findMatter.work = copyPricing.work;
            });
          }
        });
      }
    },
    refetchQueries: [
      {
        query: FIND_MATTER,
        variables: { id: matterId },
      },
    ],
    onCompleted: () => {
      setTopDownRefreshTrigger?.((prev) => prev + 1);
    },
  });

  // Only fetch accrued fees when a historical or template matter is selected
  const { data: accruedFeesData } = useQuery<
    FindAccruedFeesForMatterQuery,
    FindAccruedFeesForMatterQueryVariables
  >(FIND_ACCRUED_FEES_FOR_MATTER, {
    variables: {
      id: (viewingHistoricalData ? selectedHistoricalMatterId : selectedTemplateMatterId) || 0,
    },
    skip:
      (!viewingHistoricalData && !viewingTemplateData) ||
      (!selectedHistoricalMatterId && !selectedTemplateMatterId),
  });

  // Memoize the matterFilter with selectedOrgUnitId to prevent recreation on each render
  const memoizedMatterFilter = useMemo(
    () => ({
      ...matterFilter,
      orgUnit: selectedOrgUnitId,
    }),
    [matterFilter, selectedOrgUnitId]
  );

  // Memoize the options object to prevent recreation on each render
  const memoizedSearchOptions = useMemo(
    () => ({
      // Always use matterSearchText for both templates and historical matters
      additionalSearchText: matterSearchText?.trim() || undefined,
      autoRetry: true,
      fetchTemplates: true,
      excludeMatterId: matterId,
      fetchAccruedFees: false,
    }),
    [matterSearchText, matterId]
  );

  // State to hold search results
  const [searchResults, setSearchResults] = useState<{
    historicalData: RelevantMatter[];
    historicalMattersLoading: boolean;
    templatesData: RelevantMatter[];
    templatesLoading: boolean;
  }>({
    historicalData: [],
    historicalMattersLoading: false,
    templatesData: [],
    templatesLoading: false,
  });

  // Destructure search results for use in component
  const { historicalData, historicalMattersLoading, templatesData, templatesLoading } =
    searchResults;

  // Memoize the callback for search results to prevent recreation on each render
  const handleSearchResults = useCallback((results) => {
    setSearchResults({
      historicalData: results.historicalData,
      historicalMattersLoading: results.historicalLoading || false,
      templatesData: results.templatesData,
      templatesLoading: results.templatesLoading || false,
    });
  }, []);

  const { data: historicalMatterData, loading: historicalMatterLoading } = useFindMatterQuery({
    skip:
      (!selectedHistoricalMatterId || !viewingHistoricalData) &&
      (!selectedTemplateMatterId || !viewingTemplateData),
    id: viewingHistoricalData ? selectedHistoricalMatterId || 0 : selectedTemplateMatterId || 0,
  });

  // Update selected matter when data is loaded
  useEffect(() => {
    if (historicalMatterData?.findMatter) {
      if (selectedHistoricalMatterId && viewingHistoricalData) {
        setSelectedHistoricalMatter(historicalMatterData.findMatter as unknown as Matter);
      } else if (selectedTemplateMatterId && viewingTemplateData) {
        setSelectedTemplateMatter(historicalMatterData.findMatter as unknown as Matter);
      }
    }
  }, [
    historicalMatterData,
    selectedHistoricalMatterId,
    selectedTemplateMatterId,
    viewingHistoricalData,
    viewingTemplateData,
  ]);

  // Refs and states for scope editor functionality
  const refineScopeRef = useRef<
    ((useLibraryData: boolean, refineMode: boolean, customPrompt: string | null) => void) | null
  >(null);

  const enableAi = useContext(GlobalContext).enableAi ?? false;
  const { settings } = useSettings();
  const defaultFont = settings?.systemFont;

  // Sort and prepare phases
  const sortedPhases = (phases || [])
    .map((p) => ({
      ...p,
      autoFocus: p?.autoFocus || false,
      work: [...(p.work || [])].sort(sortById),
      tasks: [...(p.tasks || [])].sort((a, b) => (a?.order || 0) - (b?.order || 0)),
    }))
    .sort(sortByOrder);

  const tasksCountPerPhase = sortedPhases.reduce((acc, phase) => {
    acc.set(phase.id, phase.tasks?.length || 0);
    return acc;
  }, new Map<number, number>());

  // Function to handle refining
  const handleAutoGenerate = (
    useLibraryData: boolean,
    refineMode: boolean,
    customPrompt: string | null
  ) => {
    if (refineScopeRef.current) {
      refineScopeRef.current(useLibraryData, refineMode, customPrompt);
    }
  };

  // Simple hash function for signatures
  const hash = (str: string) => {
    let h = 5381;
    for (let i = 0; i < str.length; i++) {
      h = (h * 33) ^ str.charCodeAt(i);
    }
    return (h >>> 0).toString(36);
  };

  // Build one big string of all phase and task titles
  const combinedPhaseTaskText = useMemo(() => {
    if (!phases?.length) return title || "";
    const phaseTexts = phases
      .map((p) => [p.title, ...(p.tasks?.map((t) => t.title) || [])].join("|"))
      .join("||");
    return `${title || ""}::${phaseTexts}`;
  }, [phases, title]);

  // Remember the last "accepted" combined text
  const lastAcceptedTextRef = useRef<string>(combinedPhaseTaskText);

  // Create a threshold-based phase signature that only changes when edits exceed 20%
  const phaseSignature = useMemo(() => {
    const prev = lastAcceptedTextRef.current;
    if (prev === combinedPhaseTaskText) {
      return `${prev.length}::${hash(prev)}`;
    }

    // Compute edit distance and the ratio of change
    const dist = levenshtein(prev, combinedPhaseTaskText);
    const maxLen = Math.max(prev.length, combinedPhaseTaskText.length) || 1;
    const diffRatio = dist / maxLen;

    // Only update the signature if changes exceed the 20% threshold
    const changeThreshold = 0.2;

    if (diffRatio >= changeThreshold) {
      lastAcceptedTextRef.current = combinedPhaseTaskText;
      return `${combinedPhaseTaskText.length}::${hash(combinedPhaseTaskText)}`;
    }

    // Otherwise return the previous signature
    return `${prev.length}::${hash(prev)}`;
  }, [combinedPhaseTaskText]);

  // Track the last signature used for search text update
  const lastSignatureRef = useRef<string>(phaseSignature);

  // Build search text from phases
  const buildSearchTextFromSignature = useCallback(() => {
    // If there are no phases or title, return a simplified string
    if (!phases?.length) {
      return `Matter Title: ${title || ""}`;
    }

    return `Matter Title: ${title}\n\n${phases
      ?.map((phase) => {
        return `  - Phase: ${phase.title}\n    Tasks:\n${phase.tasks
          ?.map((task) => `      - ${task.title}\n`)
          .join("")}`;
      })
      .join("\n")}`;
  }, [phases, title]);

  // Flag to track if this is the first render
  const isFirstRender = useRef(true);

  // Initialize the search text on mount and update when signature changes
  useEffect(() => {
    // Always update on first render or when phase signature changes in the scope tab
    if (
      isFirstRender.current ||
      (activeSubTab === "scope" && phaseSignature !== lastSignatureRef.current)
    ) {
      lastSignatureRef.current = phaseSignature;
      setMainSearchText(buildSearchTextFromSignature());

      // Clear the first render flag after the initial update
      if (isFirstRender.current) {
        isFirstRender.current = false;
      }
    }
  }, [phaseSignature, activeSubTab, buildSearchTextFromSignature]);

  // Set mounted to true after the first render
  useEffect(() => {
    setMounted(true);
  }, []);

  // If template is true and active tab is uplifts or discounts, switch to scope tab
  useEffect(() => {
    if (template && (activeSubTab === "uplifts" || activeSubTab === "discounts")) {
      setActiveSubTab("scope");
    }
  }, [template, activeSubTab]);

  // Clear search text when switching subtabs, but only for library item filtering
  useEffect(() => {
    // Only clear the library item filter search text when not in scope tab
    if (activeSubTab !== "scope") {
      setLibrarySearchText("");
    }
  }, [activeSubTab]);

  // Toggle the sidebar collapse state
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // New handler for template matter selection
  const handleSelectTemplateMatter = (matterId: number | null) => {
    setSelectedTemplateMatterId(matterId);
    setViewingTemplateData(!!matterId);
  };

  // Handler to clear template matter selection
  const handleClearTemplateMatter = () => {
    setSelectedTemplateMatterId(null);
    setSelectedTemplateMatter(null);
    setViewingTemplateData(false);
  };

  // Handle selecting an item from the library
  const handleSelectItem = (item: LibraryItem) => {
    // Handle the selection based on the active sub-tab
    switch (activeSubTab) {
      case "assumptions":
        copyAssumption(item);
        break;
      case "exclusions":
        copyExclusion(item);
        break;
      case "discounts":
        createDiscountFromLibrary(item);
        break;
      case "uplifts":
        createUpliftFromLibrary(item as Partial<Uplift>);
        break;
      case "disbursements":
        createDisbursementFromLibrary(item);
        break;
      default:
        break;
    }
  };

  const handleCopyPhases = (matterId: number, phaseId?: number) => {
    if (phaseId) {
      copyPhaseToMatter(phaseId);
    } else {
      copyPhasesFromMatter(matterId, sortedPhases);
    }
  };

  // New handler for historical matter selection
  const handleSelectHistoricalMatter = (matterId: number | null) => {
    setSelectedHistoricalMatterId(matterId);
    setViewingHistoricalData(true);
  };

  // Handler to clear historical matter selection
  const handleClearHistoricalMatter = () => {
    setSelectedHistoricalMatterId(null);
    setSelectedHistoricalMatter(null);
    setViewingHistoricalData(false);
  };

  const assumptionsRenderer = useCallback(
    (props: LibraryItemRenderer<any>) => {
      return (
        <MemoizedSuggestedLibraryListRenderer
          {...props}
          suggestions={suggestedAssumptions}
          practiceAreas={practiceAreas}
          matterOrgUnitId={organisationalUnitId}
          selectedOrgUnitId={selectedOrgUnitId}
          setSelectedOrgUnitId={setSelectedOrgUnitId}
          selectedItems={matterAssumptions}
          subTab="assumptions"
          items={Array.isArray(props.items) ? (props.items as TabItemMap["assumptions"][]) : []}
        />
      );
    },
    [
      suggestedAssumptions,
      practiceAreas,
      organisationalUnitId,
      selectedOrgUnitId,
      setSelectedOrgUnitId,
      matterAssumptions,
    ]
  );

  const exclusionsRenderer = useCallback(
    (props: LibraryItemRenderer<any>) => {
      return (
        <MemoizedSuggestedLibraryListRenderer
          {...props}
          suggestions={suggestedExclusions}
          practiceAreas={practiceAreas}
          matterOrgUnitId={organisationalUnitId}
          selectedOrgUnitId={selectedOrgUnitId}
          setSelectedOrgUnitId={setSelectedOrgUnitId}
          selectedItems={matterExclusions}
          subTab="exclusions"
          items={Array.isArray(props.items) ? (props.items as TabItemMap["exclusions"][]) : []}
        />
      );
    },
    [
      suggestedExclusions,
      practiceAreas,
      organisationalUnitId,
      selectedOrgUnitId,
      setSelectedOrgUnitId,
      matterExclusions,
    ]
  );

  const disbursementsRenderer = useCallback(
    (props: LibraryItemRenderer<any>) => {
      return (
        <MemoizedLibraryListRenderer
          {...props}
          subTab="disbursements"
          items={Array.isArray(props.items) ? (props.items as TabItemMap["disbursements"][]) : []}
          currencySymbol={currencySymbol}
          historical={false}
        />
      );
    },
    [currencySymbol]
  );

  const upliftsRenderer = useCallback((props: LibraryItemRenderer<any>) => {
    return (
      <MemoizedGroupedListRenderer
        {...props}
        subTab="uplifts"
        items={
          !Array.isArray(props.items)
            ? (props.items as Record<string, TabItemMap["uplifts"][]>)
            : {}
        }
      />
    );
  }, []);

  const discountsRenderer = useCallback((props: LibraryItemRenderer<any>) => {
    return <MemoizedSimpleStringListRenderer {...props} />;
  }, []);

  // Memoize the entire libraryRenderers object
  const libraryRenderers = useMemo(
    () => ({
      assumptions: assumptionsRenderer,
      exclusions: exclusionsRenderer,
      disbursements: disbursementsRenderer,
      uplifts: upliftsRenderer,
      discounts: discountsRenderer,
    }),
    [
      assumptionsRenderer,
      exclusionsRenderer,
      disbursementsRenderer,
      upliftsRenderer,
      discountsRenderer,
    ]
  );

  // Memoize the library items to prevent re-creation on each render
  const libraryItems = useMemo(
    () => ({
      scope: templatesData || [], // Pass the templatesData to be used in the scope tab
      assumptions: libraryAssumptions || [],
      exclusions: libraryExclusions || [],
      disbursements: libraryDisbursements || [],
      ...(template
        ? {}
        : {
            uplifts: libraryUplifts || [],
            discounts: libraryDiscounts || [],
          }),
    }),
    [
      templatesData, // Add templatesData to the dependency array
      libraryAssumptions,
      libraryExclusions,
      libraryDisbursements,
      libraryUplifts,
      libraryDiscounts,
      template,
    ]
  );

  // Memoize the selected library items
  const selectedLibraryItems = useMemo(
    () => ({
      scope: [], // Scope doesn't need selected items
      assumptions: matterAssumptions,
      exclusions: matterExclusions,
      disbursements,
      ...(template
        ? {}
        : {
            uplifts,
            discounts: discounts.map((discount) => discount.reason),
          }),
    }),
    [matterAssumptions, matterExclusions, disbursements, uplifts, discounts, template]
  );

  // Sort discounts by order for reordering functionality
  const sortedDiscounts = [...discounts].sort((a, b) => a.order - b.order);
  const sortedUplifts = [...uplifts].sort((a, b) => a.order - b.order);
  const sortedDisbursements = [...disbursements].sort((a, b) => a.order - b.order);
  const sortedAssumptions = [...matterAssumptions].sort((a, b) => a.order - b.order);
  const sortedExclusions = [...matterExclusions].sort((a, b) => a.order - b.order);

  // Define the tabs items for Ant Design Tabs component
  // TODO JS: somehow get the tab path in the url
  const tabItems = [
    {
      key: "scope",
      label: "Scope",
    },
    {
      key: "assumptions",
      label: "Assumptions",
    },
    {
      key: "exclusions",
      label: "Exclusions",
    },
    {
      key: "disbursements",
      label: "Disbursements",
    },
    ...(!template
      ? [
          {
            key: "uplifts",
            label: "Uplifts",
          },
          {
            key: "discounts",
            label: "Discounts",
          },
        ]
      : []),
  ];

  const handlePinToMatter = (matterToPinId: number) => {
    updateMatter({
      variables: {
        id: matterToPinId,
        data: {
          pinnedTo: {
            // If already pinned, disconnect it, otherwise connect it
            ...(pinnedData?.filteredMatters?.matters.some((m) => m.id === matterToPinId)
              ? { disconnect: [{ id: matterId }] }
              : { connect: [{ id: matterId }] }),
          },
        },
      },
      refetchQueries: [
        {
          query: FILTER_MATTERS,
          variables: {
            input: {
              filter: {
                pinnedToId: matterId,
              },
              limit: 100,
            },
          },
        },
      ],
    });
  };

  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);

  // Handler for opening the matter detail modal
  const handleOpenMatterDetailModal = () => {
    if (selectedHistoricalMatter) {
      setIsDetailModalVisible(true);
    }
  };

  const handleCloseMatterDetailModal = () => {
    setIsDetailModalVisible(false);
  };

  if (!mounted) {
    return (
      <div
        className={scopeAndAdjustmentsContainerClassName}
        style={{ position: "relative", height: "600px" }}
      ></div>
    );
  }

  return (
    <>
      <MatterSearchProvider
        searchText={mainSearchText}
        matterFilter={memoizedMatterFilter}
        searchOptions={memoizedSearchOptions}
        onDataLoaded={handleSearchResults}
      />
      <div className={scopeAndAdjustmentsContainerClassName}>
        <div className={scopeAndAdjustmentsContentClassName}>
          {/* Left sidebar for library items */}
          <div
            className={`${scopeLibraryWrapperClassName} ${
              sidebarCollapsed ? scopeLibraryWrapperCollapsedClassName : ""
            }`}
          >
            {/* Toggle button for sidebar */}
            <Button
              type="text"
              icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={toggleSidebar}
              className={scopeLibraryToggleClassName}
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              style={{ marginTop: sidebarCollapsed ? "0px" : "10px" }}
            />

            {/* Only render library content when not collapsed */}
            {!sidebarCollapsed && (
              <MemoizedScopeAndAdjustmentsLibrary
                activeSubTab={activeSubTab}
                organisationalUnitId={organisationalUnitId}
                onSelectItem={handleSelectItem}
                onCopyPhases={handleCopyPhases}
                // Search text state
                searchTextState={{
                  librarySearchText,
                  setLibrarySearchText,
                  matterSearchText,
                  setMatterSearchText,
                }}
                // Consolidated matter selection
                selectedMatterInfo={
                  viewingHistoricalData && selectedHistoricalMatter
                    ? {
                        matterId: selectedHistoricalMatter?.id,
                        matter: selectedHistoricalMatter,
                        matterType: "historical",
                        viewingData: viewingHistoricalData,
                        loading: historicalMatterLoading,
                        onTitleClick: handleOpenMatterDetailModal,
                      }
                    : viewingTemplateData && selectedTemplateMatter
                    ? {
                        matterId: selectedTemplateMatter?.id,
                        matter: selectedTemplateMatter,
                        matterType: "template",
                        viewingData: viewingTemplateData,
                        loading: templateMatterLoading,
                      }
                    : undefined
                }
                onSelectMatter={(matterId, matterType) => {
                  if (matterType === "template") {
                    handleSelectTemplateMatter(matterId);
                  } else {
                    handleSelectHistoricalMatter(matterId);
                  }
                }}
                onClearSelectedMatter={() => {
                  handleClearTemplateMatter();
                  handleClearHistoricalMatter();
                }}
                onPinToMatter={handlePinToMatter}
                // Matter data
                matterData={{
                  historical: {
                    data: historicalData,
                    loading: historicalMattersLoading,
                  },
                  favourites: {
                    data: favouritesData as unknown as RelevantMatter[],
                    loading: favouritesLoading,
                  },
                  templates: {
                    loading: templatesLoading,
                  },
                  pinned: {
                    data: pinnedData?.filteredMatters?.matters as unknown as RelevantMatter[],
                    loading: pinnedLoading,
                  },
                }}
                libraryRenderers={libraryRenderers}
                libraryItems={libraryItems}
                selectedLibraryItems={selectedLibraryItems}
                practiceAreas={practiceAreas}
                selectedOrgUnitId={selectedOrgUnitId}
                setSelectedOrgUnitId={setSelectedOrgUnitId}
                currencySymbol={currencySymbol}
                // Pagination
                pagination={{
                  enabled: true,
                  currentPage: favouritesPage,
                  pageSize: favouritesPageSize,
                  totalCount: favouritesCount,
                  onPageChange: onChangeFavouritesPage,
                }}
              />
            )}
          </div>

          <div className={scopeAndAdjustmentsMainClassName}>
            {/* Sub-tabs navigation - using new items pattern */}
            <div className={scopeSubTabsClassName}>
              <Tabs
                activeKey={activeSubTab}
                onChange={(key) => setActiveSubTab(key as SubTabType)}
                type="line"
                items={tabItems}
                tabBarStyle={{ margin: 0 }}
                size="small"
                renderTabBar={(props, DefaultTabBar) => <DefaultTabBar {...props} />}
              />
            </div>

            <TabCache active={activeSubTab === "scope"}>
              <MemoizedMatterScopeEditor
                matterId={matterId}
                title={title}
                currencySymbol={currencySymbol}
                countryCode={countryCode}
                template={template}
                phases={sortedPhases}
                teamMembers={teamMembers || []}
                onAutoFocus={autoFocusOff}
                defaultFont={defaultFont}
                onCreate={async () => {
                  await handleCreatePhase();
                  handleRefetchSuggestions && handleRefetchSuggestions();
                }}
                onCreatePhases={async (phases: { phase: string; tasks: string[] }[]) => {
                  await handleCreatePhases(phases, sortedPhases, defaultFont);
                  handleRefetchSuggestions && handleRefetchSuggestions();
                }}
                onUpdate={async (phase: PhaseBase) => {
                  await handleUpdatePhase(phase, sortedPhases);
                  handleRefetchSuggestions && handleRefetchSuggestions();
                }}
                onDelete={handleDeletePhase}
                onCreateTask={(phaseId) =>
                  handleCreateTask(phaseId, tasksCountPerPhase.get(phaseId) || 0)
                }
                onCreateTasks={(taskNames: string[], phaseId) =>
                  handleCreateTasks(taskNames, phaseId, sortedPhases, defaultFont)
                }
                onDeleteTasks={(phaseId) => handleDeleteTasks(phaseId, sortedPhases)}
                onReset={() => handleResetPhases(sortedPhases)}
                onDeleteTask={handleDeleteTask}
                onUpdateTask={handleUpdateTask}
                onCreateFeeEarner={handleCreateFeeEarner}
                onDeleteFeeEarner={handleDeleteFeeEarner}
                onUpdateFeeEarner={(work, phaseId, isTimeEntryUpdate) =>
                  handleUpdateFeeEarner(work, phaseId, isTimeEntryUpdate, sortedPhases)
                }
                pricingLevel={pricingLevel}
                pricingType={pricingType}
                onPriceChange={({ phaseId, value, pricingType, field }) => {
                  if (typeof phaseId === "number" && value !== null) {
                    handleUpdatePhasePrice(phaseId, value, pricingType, field, sortedPhases);
                  }
                }}
                onTaskReorder={(activeId, overId) => {
                  if (activeId && overId) {
                    const activePhase = sortedPhases.find((phase) =>
                      phase.tasks?.some((task) => task.id === activeId)
                    );

                    if (activePhase?.tasks) {
                      onReorderTask(activePhase.tasks, activeId, overId);
                    }
                  }
                }}
                onPhaseReorder={(activeId, overId) =>
                  onReorderPhase(sortedPhases, activeId, overId)
                }
                handleAutoGenerate={handleAutoGenerate}
                useAi={enableAi}
                onPhaseListMount={(refineFn) => {
                  refineScopeRef.current = refineFn;
                }}
              />
            </TabCache>

            <TabCache active={activeSubTab === "assumptions"}>
              <MemoizedMatterAssumptionsEditor
                loading={matterAssumptionsLoading}
                assumptions={sortedAssumptions}
                onAddNew={addAssumption}
                onSave={saveAssumption}
                onDelete={deleteAssumption}
                onReorder={(activeId, overId) =>
                  reorderAssumptions(sortedAssumptions, activeId, overId)
                }
              />
            </TabCache>

            <TabCache active={activeSubTab === "exclusions"}>
              <MemoizedMatterExclusionsEditor
                loading={matterExclusionsLoading}
                exclusions={sortedExclusions}
                onAddNew={addExclusion}
                onSave={saveExclusion}
                onDelete={deleteExclusion}
                onReorder={(activeId, overId) =>
                  reorderExclusions(sortedExclusions, activeId, overId)
                }
              />
            </TabCache>

            <TabCache active={activeSubTab === "disbursements"}>
              <MemoizedMatterDisbursementsEditor
                disbursements={sortedDisbursements}
                onUpdate={updateDisbursement}
                onDelete={deleteDisbursement}
                onCreate={createDisbursementFromLibrary}
                libraryDisbursements={libraryDisbursements || []}
                onReorder={(activeId, overId) =>
                  onReorderDisbursements(sortedDisbursements, activeId, overId)
                }
                onAutoFocus={autoFocusOffDisbursements}
                currencySymbol={currencySymbol}
                taxLevelPercent={taxLevelPercent}
                taxLabel={taxLabel}
                excludeTax={excludeTax}
              />
            </TabCache>

            {!template && (
              <>
                <TabCache active={activeSubTab === "uplifts"}>
                  <MemoizedMatterUpliftsEditor
                    uplifts={sortedUplifts}
                    onUpdate={updateUplift}
                    onDelete={deleteUplift}
                    libraryUplifts={libraryUplifts}
                    onReorder={(activeId, overId) =>
                      onReorderUplifts(sortedUplifts, activeId, overId)
                    }
                    onAutoFocus={autoFocusOffUplifts}
                    currencySymbol={currencySymbol}
                  />
                </TabCache>

                <TabCache active={activeSubTab === "discounts"}>
                  <MemoizedMatterDiscountsEditor
                    discounts={sortedDiscounts}
                    onUpdate={updateDiscount}
                    onDelete={deleteDiscount}
                    onCreate={createDiscountFromLibrary}
                    libraryDiscounts={libraryDiscounts}
                    currencySymbol={currencySymbol}
                    onReorder={(activeId, overId) =>
                      onReorderDiscounts(sortedDiscounts, activeId, overId)
                    }
                    onAutoFocus={autoFocusOffDiscounts}
                  />
                </TabCache>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Historical Matter Detail Modal */}
      {selectedHistoricalMatter && (
        <Modal
          title={null}
          open={isDetailModalVisible}
          onCancel={handleCloseMatterDetailModal}
          footer={null}
          width={1300}
          closable={false}
          maskClosable={true}
          bodyStyle={{ padding: "10px 24px 24px 24px" }}
        >
          <RelevantMatterPage
            showAlert={false}
            matter={selectedHistoricalMatter as unknown as RelevantMatter}
            onDeselectMatter={handleCloseMatterDetailModal}
            maxHeight="60vh"
            accruedFees={accruedFeesData?.findAccruedFeesForMatter}
            onCopyScope={
              selectedHistoricalMatter?.id
                ? () => {
                    copyScope({
                      variables: {
                        scopeMatterId: selectedHistoricalMatter.id,
                        targetMatterId: matterId,
                      },
                    });
                  }
                : undefined
            }
            onCopyResourcing={
              selectedHistoricalMatter?.id
                ? (rateReplacements) => {
                    copyResourcing({
                      variables: {
                        resourcingMatterId: selectedHistoricalMatter.id,
                        targetMatterId: matterId,
                        rateReplacements,
                      },
                    });
                  }
                : undefined
            }
            onCopyPricing={
              selectedHistoricalMatter?.id
                ? (rateReplacements) => {
                    copyPricing({
                      variables: {
                        pricingMatterId: selectedHistoricalMatter.id,
                        targetMatterId: matterId,
                        copyScope: false,
                        copyResourcing: false,
                        rateReplacements,
                      },
                    });
                  }
                : undefined
            }
            onCopyAll={
              selectedHistoricalMatter?.id
                ? (rateReplacements) => {
                    copyPricing({
                      variables: {
                        pricingMatterId: selectedHistoricalMatter.id,
                        targetMatterId: matterId,
                        copyScope: true,
                        copyResourcing: true,
                        rateReplacements,
                      },
                    });
                  }
                : undefined
            }
            onPinToMatter={(matterToPinId: number) => {
              handlePinToMatter(matterToPinId);
              handleCloseMatterDetailModal();
            }}
            pinned={pinnedData?.filteredMatters?.matters.some(
              (m) => m.id === selectedHistoricalMatter.id
            )}
          />
        </Modal>
      )}
    </>
  );
};
